import React from 'react';
import { useNavigate } from 'react-router-dom';
import Header from './Header';
import Footer from './Footer';
import {
  Check, Zap, Crown, Building, ArrowRight,
  Users, Shield, AlertCircle, FileText,
  Bot, ShieldCheck, Monitor, Headphones, HelpCircle,
} from 'lucide-react';

// Pricing — Swiss Minimalist + Pastel theme (mirrors Home / Features).
// Pastels: sage #D4E0DA · peach #EBD8C8 · lavender #D9CFD9
// Single global font: Inter (.font-display / .font-body / .font-label all map to Inter)
//
// Positioning: Nova is an AI-powered compliance and certification platform. Subscription pricing
// covers Nova only. Certification is always issued by independent auditors and
// their fees are separate.

const FRAMEWORKS = [
  { label: 'SOC 2', id: 'soc-2' },
  { label: 'ISO 27001', id: 'iso-27001' },
  { label: 'PCI DSS', id: 'pci-dss' },
  { label: 'GDPR', id: 'gdpr' },
  { label: 'HIPAA', id: 'hipaa' },
];

const Pricing = () => {
  const navigate = useNavigate();

  const plans = [
    {
      name: 'Standard',
      slug: 'standard',
      icon: Zap,
      tint: '#EBD8C8',
      monthlyPrice: '499',
      description: 'For small teams preparing for their first audit.',
      buttonText: 'Start Free Trial',
      systems: 'Up to 10',
      framework: 'All compliance frameworks',
      support: 'Email support + weekly guidance',
      extras: [
        'Automated evidence collection',
        'Control mapping',
        'AWS, GitHub, Google integrations',
        'Audit-ready documentation',
      ],
    },
    {
      name: 'Pro',
      slug: 'pro',
      icon: Crown,
      tint: '#D4E0DA',
      popular: true,
      monthlyPrice: '999',
      description: 'For growing companies with ongoing compliance needs.',
      buttonText: 'Start Free Trial',
      systems: 'Up to 25',
      framework: 'All compliance frameworks',
      support: 'Priority support + weekly guidance',
      extras: [
        'Continuous monitoring',
        'Cloud & DevOps integrations',
        'Advanced gap analysis',
        'Priority preparation support',
        'Audit-ready documentation',
      ],
    },
    {
      name: 'Enterprise',
      slug: 'enterprise',
      icon: Building,
      tint: '#D9CFD9',
      isEnterprise: true,
      description: 'Unlimited systems, custom integrations and tailored support for complex compliance programs.',
      buttonText: 'Talk to Sales',
      framework: 'All compliance frameworks',
      support: 'Dedicated compliance manager',
      support2: 'Priority support',
      extras: [
        'Strategic compliance reviews',
        'Custom integrations',
        'Dedicated auditor coordination',
      ],
    },
  ];

  // What's included with Nova — Section 1: Compliance Automation
  const novaIncludes = [
    'Evidence collection',
    'Control mapping',
    'Gap analysis',
    'Audit-ready documentation',
    'Audit preparation',
  ];
  // What's included with Nova — Section 2: Independent Auditor Coordination
  const auditorIncludes = [
    'Trusted audit partners',
    'Auditor selection assistance',
    'Coordination and scheduling',
    'Evidence sharing support',
    'End-to-end guidance',
  ];

  const faqs = [
    {
      q: 'Are auditor fees included?',
      a: 'No. Independent auditor fees are billed separately and vary depending on audit scope and framework requirements. Nova works with trusted independent audit partners and coordinates the process for you.',
    },
    {
      q: 'Can I use my own auditor?',
      a: 'Yes. You can work with your existing auditor or choose one of Nova\u2019s recommended partners.',
    },
    {
      q: 'Does Nova issue certifications?',
      a: 'No. Certifications and audit reports are always issued by independent auditors.',
    },
  ];

  const terms = [
    {
      tint: '#D4E0DA',
      icon: Monitor,
      title: 'What are "systems"?',
      bodyTop: 'Systems are the technology platforms, tools, and services in your compliance scope. Common examples:',
      bullets: [
        ['Cloud Infrastructure', 'AWS, Azure, Google Cloud Platform (GCP)'],
        ['Development & Version Control', 'GitHub, GitLab, Bitbucket'],
        ['Communication', 'Slack, Microsoft Teams, Google Workspace'],
        ['Identity & Access', 'Okta, Auth0, Azure AD'],
        ['Monitoring & Security', 'Datadog, Splunk, PagerDuty, Cloudflare'],
        ['Databases', 'MySQL, PostgreSQL, MongoDB, Redis'],
      ],
    },
    {
      tint: '#EBD8C8',
      icon: Shield,
      title: 'Scope determination',
      body: 'Pricing tiers are based primarily on the number of systems within your compliance scope. Organisations with unusually complex requirements may require a higher-tier plan.',
    },
    {
      tint: '#D9CFD9',
      icon: AlertCircle,
      title: 'Observation period',
      body: 'Most customers achieve audit readiness within 3-6 months depending on their starting maturity and framework requirements.',
    },
    {
      tint: '#D4E0DA',
      icon: Users,
      title: 'Auditor fees',
      body: 'Independent auditor fees are billed separately and vary depending on framework requirements and audit scope.',
    },
    {
      tint: '#EBD8C8',
      icon: FileText,
      title: 'Contract term',
      body: 'Billed annually.',
    },
    {
      tint: '#D9CFD9',
      icon: ShieldCheck,
      title: 'Final report',
      body: 'Audit reports are issued by independent auditors. Nova coordinates the engagement and supports the process end-to-end.',
    },
  ];

  return (
    <div className="min-h-screen bg-[#FAFAFA] dark:bg-[#050505] text-[#0A0A0A] dark:text-[#F5F5F5] font-body">
      <Header />

      {/* ===================== HERO ===================== */}
      <section
        className="bg-[#D4E0DA]/55 dark:bg-[#101F18]/55 pt-24 md:pt-32 pb-24 md:pb-32 px-6 border-b border-black/10 dark:border-white/10 min-h-[480px] md:min-h-[640px]"
        data-testid="pricing-hero"
      >
        <div className="max-w-5xl mx-auto text-center w-full">
          <p className="font-label text-[11px] uppercase tracking-[0.3em] text-black/55 dark:text-white/55 mb-8">
            Simple Pricing
          </p>
          <h1 className="font-display font-semibold leading-[1.25] tracking-tight text-2xl sm:text-3xl lg:text-4xl mb-8">
            Simple pricing for
            <br />
            compliance and certification
          </h1>
          <p className="font-body text-base md:text-lg text-black/70 dark:text-white/70 max-w-2xl mx-auto leading-relaxed mb-10">
            Nova automates evidence collection and audit preparation, then connects you with trusted independent auditors whose fees are billed separately.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-3 font-label text-[11px] uppercase tracking-[0.18em] text-black/60 dark:text-white/60">
            {[
              'Evidence automation',
              'All compliance frameworks',
              'Auditor fees billed separately',
            ].map((label) => (
              <span key={label} className="inline-flex items-center gap-2">
                <Check className="w-3.5 h-3.5 text-[#0A0A0A] dark:text-[#F5F5F5]" strokeWidth={1.5} />
                {label}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* ===================== TWO PILLARS — PREPARATION + COORDINATION ===================== */}
      <section
        className="bg-[#FAFAFA] dark:bg-[#050505] border-b border-black/10 dark:border-white/10 px-6 py-16 md:py-20"
        data-testid="pricing-pillars"
      >
        <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
          {[
            {
              testid: 'pillar-preparation',
              icon: Bot,
              tint: '#D4E0DA',
              title: 'Compliance Preparation',
              subtitle: 'Included with every Nova subscription',
              items: novaIncludes,
            },
            {
              testid: 'pillar-coordination',
              icon: ShieldCheck,
              tint: '#EBD8C8',
              title: 'Independent Auditor Coordination',
              subtitle: 'Supported by Nova',
              items: auditorIncludes,
            },
          ].map((pillar) => {
            const Icon = pillar.icon;
            return (
              <div
                key={pillar.testid}
                data-testid={pillar.testid}
                className="rounded-2xl border border-black/10 dark:border-white/10 bg-white dark:bg-[#111111] px-8 py-10 md:p-11 transition-colors duration-500 hover:border-black/25 dark:hover:border-white/25"
              >
                <div className="flex items-center gap-4 mb-6">
                  <div
                    className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0 nv-tint"
                    style={{ backgroundColor: pillar.tint }}
                  >
                    <Icon className="w-5 h-5 text-[#0A0A0A] dark:text-[#F5F5F5]" strokeWidth={1.5} />
                  </div>
                  <div>
                    <h3 className="font-display font-semibold text-xl md:text-2xl leading-tight">{pillar.title}</h3>
                    <p className="font-label text-[10px] uppercase tracking-[0.2em] text-black/50 dark:text-white/55 mt-1">
                      {pillar.subtitle}
                    </p>
                  </div>
                </div>
                <ul className="space-y-3.5">
                  {pillar.items.map((item) => (
                    <li key={item} className="flex items-start gap-3">
                      <Check className="w-4 h-4 mt-1 shrink-0 text-black/55 dark:text-white/55" strokeWidth={1.5} />
                      <span className="text-base font-body text-black/80 dark:text-white/85 leading-relaxed">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      </section>

      {/* ===================== PRICING CARDS ===================== */}
      <section className="px-6 py-24 md:py-28" data-testid="pricing-cards">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8 max-w-6xl mx-auto">
            {plans.map((plan) => {
              const Icon = plan.icon;
              return (
                <div
                  key={plan.slug}
                  data-testid={`plan-card-${plan.slug}`}
                  className={`relative rounded-2xl border ${plan.popular ? 'border-black/30 dark:border-white/30 bg-[#D4E0DA]/30 dark:bg-[#101F18]/30' : 'border-black/10 dark:border-white/10 bg-[#FAFAFA] dark:bg-[#050505]'} px-7 py-9 flex flex-col transition-colors duration-500 hover:border-black/30 dark:hover:border-white/30`}
                >
                  {plan.popular && (
                    <span className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-1 bg-[#0A0A0A] dark:bg-[#141414] text-[#FAFAFA] font-label uppercase tracking-[0.22em] text-[10px] px-3 py-1 rounded-full">
                      Most Popular
                    </span>
                  )}

                  <div className="flex items-start justify-between mb-6">
                    <div
                      className="w-11 h-11 rounded-xl flex items-center justify-center nv-tint"
                      style={{ backgroundColor: plan.tint + 'CC' }}
                    >
                      <Icon className="w-5 h-5 text-[#0A0A0A] dark:text-[#F5F5F5]" strokeWidth={1.5} />
                    </div>
                  </div>

                  <h3 className="font-display text-2xl text-[#0A0A0A] dark:text-[#F5F5F5] leading-tight mb-1.5">{plan.name}</h3>
                  <p className="text-sm text-black/65 dark:text-white/65 leading-snug mb-6 min-h-[40px]">
                    {plan.isEnterprise
                      ? plan.description
                      : (
                        <span className="text-[#0A0A0A] dark:text-[#F5F5F5]">{plan.systems} systems</span>
                      )}
                  </p>

                  <div className="mb-7 pb-7 border-b border-black/10 dark:border-white/10">
                    {plan.isEnterprise ? (
                      <>
                        <p className="font-display text-3xl text-[#0A0A0A] dark:text-[#F5F5F5] leading-none">Custom</p>
                        <p className="text-sm text-[#0A0A0A] dark:text-[#F5F5F5] mt-3">Unlimited systems</p>
                        <p className="text-xs text-black/55 dark:text-white/55 mt-1.5">
                          Nova subscription · auditor fees billed separately
                        </p>
                      </>
                    ) : (
                      <>
                        <div className="flex items-baseline gap-2">
                          <span className="font-label uppercase tracking-[0.2em] text-[10px] text-black/55 dark:text-white/55">USD</span>
                          <span className="font-display text-4xl text-[#0A0A0A] dark:text-[#F5F5F5] leading-none">{plan.monthlyPrice}</span>
                          <span className="font-label uppercase tracking-[0.18em] text-[10px] text-black/55 dark:text-white/55">/ month</span>
                        </div>
                        <p className="text-xs text-black/55 dark:text-white/55 mt-3">
                          Nova subscription · auditor fees billed separately
                        </p>
                      </>
                    )}
                  </div>

                  <div className="mb-8 flex-1">
                    <div className="space-y-3">
                      <Spec icon={Shield} label={plan.framework} />
                      <Spec icon={Headphones} label={plan.support} />
                      {plan.support2 && <Spec icon={Headphones} label={plan.support2} />}
                    </div>

                    <ul className="pt-4 mt-4 border-t border-black/10 dark:border-white/10 space-y-2.5">
                      {plan.extras.map((extra) => (
                        <li key={extra} className="flex items-start gap-2.5">
                          <Check className="w-3.5 h-3.5 mt-1 shrink-0 text-black/55 dark:text-white/55" strokeWidth={1.5} />
                          <span className="text-sm leading-snug text-black/75 dark:text-white/75">{extra}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <button
                    type="button"
                    onClick={() => navigate(plan.isEnterprise ? '/contact' : '/register')}
                    data-testid={`select-${plan.slug}`}
                    className="font-label uppercase tracking-[0.18em] text-[11px] bg-[#0F62FE] dark:bg-[#2E79FF] text-white hover:bg-[#0A4FCC] dark:hover:bg-[#5C97FF] py-3.5 px-5 rounded-full transition-colors duration-500 inline-flex items-center justify-center gap-2 whitespace-nowrap"
                  >
                    {plan.buttonText}
                    <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.5} />
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ===================== FRAMEWORKS SUPPORTED ===================== */}
      <section className="px-6 pb-24 md:pb-32" data-testid="pricing-frameworks">
        <div className="max-w-5xl mx-auto text-center">
          <h2 className="font-display font-semibold text-3xl md:text-4xl leading-[1.1] tracking-tight mb-5">
            Frameworks Supported
          </h2>
          <p className="font-body text-base md:text-lg text-black/65 dark:text-white/65 max-w-2xl mx-auto leading-relaxed mb-12">
            Every Nova plan supports the same frameworks. Framework availability never depends on the plan you choose.
          </p>
          <div
            data-testid="pricing-framework-strip"
            className="flex flex-wrap items-center justify-center gap-3 md:gap-4"
          >
            {FRAMEWORKS.map((fw) => (
              <span
                key={fw.id}
                data-testid={`pricing-framework-${fw.id}`}
                className="font-label text-sm tracking-[0.12em] text-black/65 dark:text-white/65 bg-black/[0.025] dark:bg-white/[0.04] border border-black/10 dark:border-white/10 px-6 py-3 rounded-full hover:border-black/25 dark:hover:border-white/25 hover:text-black/85 dark:hover:text-white/90 transition-colors duration-300"
              >
                {fw.label}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* ===================== CERTIFICATION NOTE ===================== */}
      <section className="px-6 pb-24 md:pb-32" data-testid="pricing-certification-note">
        <div className="max-w-3xl mx-auto rounded-2xl border border-black/10 dark:border-white/10 bg-[#D9CFD9]/30 dark:bg-[#1E1724]/30 px-8 py-10 md:p-12 text-center">
          <div className="w-11 h-11 rounded-xl bg-[#D9CFD9] dark:bg-[#1E1724] flex items-center justify-center mx-auto mb-5">
            <ShieldCheck className="w-5 h-5 text-[#0A0A0A] dark:text-[#F5F5F5]" strokeWidth={1.5} />
          </div>
          <p className="font-body text-base md:text-lg text-black/75 dark:text-white/75 leading-relaxed">
            Certification is issued by independent auditors. Auditor fees are billed separately and vary depending on scope and framework requirements. Nova handles preparation and coordinates the engagement with trusted audit partners.
          </p>
        </div>
      </section>

      {/* ===================== PRICING FAQ ===================== */}
      <section className="px-6 pb-24 md:pb-32" data-testid="pricing-faq">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
          <h2 className="font-display font-semibold text-3xl md:text-4xl leading-[1.1] tracking-tight">
              Pricing FAQ
            </h2>
          </div>

          <ul>
            {faqs.map((f) => (
              <li
                key={f.q}
                data-testid={`pricing-faq-${f.q.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')}`}
                className="grid grid-cols-[44px_1fr] gap-5 items-start border-t border-black/10 dark:border-white/10 last:border-b py-7 md:py-8"
              >
                <div className="w-10 h-10 rounded-xl bg-[#D9CFD9]/80 dark:bg-[#1E1724]/80 flex items-center justify-center mt-0.5">
                  <HelpCircle className="w-5 h-5 text-[#0A0A0A] dark:text-[#F5F5F5]" strokeWidth={1.5} />
                </div>
                <div>
                  <h4 className="font-display font-semibold text-lg md:text-xl text-[#0A0A0A] dark:text-[#F5F5F5] leading-tight mb-2">
                    {f.q}
                  </h4>
                  <p className="font-body text-base text-black/70 dark:text-white/70 leading-relaxed">{f.a}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* ===================== ADDITIONAL INFO & TERMS ===================== */}
      <section className="px-6 pb-24 md:pb-32" data-testid="pricing-terms">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-14">
            <p className="font-label text-[11px] uppercase tracking-[0.25em] text-black/55 dark:text-white/55 mb-3">
              Pricing Details
            </p>
            <h2 className="font-display font-semibold text-3xl md:text-4xl leading-[1.1] tracking-tight mb-4">
              Additional information &amp; terms
            </h2>
            <p className="font-body text-base md:text-lg text-black/65 dark:text-white/65 max-w-2xl mx-auto">
              Important details about pricing, scope, and contract terms.
            </p>
          </div>

          <ul>
            {terms.map((t) => {
              const Icon = t.icon;
              return (
                <li
                  key={t.title}
                  className="grid grid-cols-[44px_1fr] md:grid-cols-[52px_1fr] gap-5 md:gap-7 items-start border-t border-black/10 dark:border-white/10 last:border-b py-7 md:py-8"
                >
                  <div
                    className="w-10 h-10 md:w-11 md:h-11 rounded-xl flex items-center justify-center mt-1 nv-tint"
                    style={{ backgroundColor: t.tint + 'CC' }}
                  >
                    <Icon className="w-4 h-4 md:w-5 md:h-5 text-[#0A0A0A] dark:text-[#F5F5F5]" strokeWidth={1.5} />
                  </div>
                  <div>
                    <h4 className="font-display text-lg md:text-xl text-[#0A0A0A] dark:text-[#F5F5F5] leading-tight mb-2">
                      {t.title}
                    </h4>
                    {t.bodyTop && (
                      <p className="text-sm md:text-base text-black/70 dark:text-white/70 leading-relaxed mb-2">{t.bodyTop}</p>
                    )}
                    {t.bullets && (
                      <ul className="text-sm md:text-base text-black/70 dark:text-white/70 leading-relaxed space-y-1.5 mt-2">
                        {t.bullets.map(([k, v]) => (
                          <li key={k} className="flex flex-col sm:flex-row sm:gap-2">
                            <span className="font-medium text-[#0A0A0A] dark:text-[#F5F5F5]">{k}:</span>
                            <span>{v}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                    {t.body && (
                      <p className="text-sm md:text-base text-black/70 dark:text-white/70 leading-relaxed">{t.body}</p>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      </section>

      {/* ===================== FINAL CTA (INVERTED) ===================== */}
      <section
        className="bg-[#0A0A0A] dark:bg-[#141414] py-24 md:py-32 px-6 text-center"
        data-testid="pricing-cta-section"
      >
        <div className="max-w-4xl mx-auto">
          <h2 className="font-display font-semibold text-3xl md:text-4xl text-[#FAFAFA] mb-10 leading-[1.1] tracking-tight">
            Start Your Compliance and Certification Journey
          </h2>
          <p className="font-body text-base md:text-lg text-white/70 mb-12 max-w-2xl mx-auto leading-relaxed">
            Begin with a free 60-minute assessment to understand your compliance gaps and prepare for independent certification.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <button
              type="button"
              onClick={() => navigate('/register')}
              data-testid="pricing-cta-start"
              className="font-label bg-[#0F62FE] dark:bg-[#2E79FF] text-white hover:bg-[#0A4FCC] dark:hover:bg-[#5C97FF] uppercase tracking-[0.2em] text-xs px-8 py-4 rounded-full shadow-sm hover:shadow-md transition-all duration-500"
            >
              Start Free Trial
            </button>
            <button
              type="button"
              onClick={() => navigate('/contact')}
              data-testid="pricing-cta-contact"
              className="font-label border border-white/25 bg-transparent text-[#FAFAFA] hover:bg-[#FAFAFA] dark:hover:bg-[#050505] hover:text-[#0A0A0A] dark:hover:text-[#F5F5F5] uppercase tracking-[0.2em] text-xs px-8 py-4 rounded-full transition-all duration-500"
            >
              Contact Sales
            </button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

const Spec = ({ icon: Icon, label }) => (
  <div className="flex items-center gap-2.5">
    <Icon className="w-3.5 h-3.5 shrink-0 text-black/40 dark:text-white/45" strokeWidth={1.5} />
    <span className="text-sm text-black/75 dark:text-white/75 leading-snug">{label}</span>
  </div>
);

export default Pricing;
